/**
 * AudioManager - Synthesizes Pac-Man sounds using Web Audio API
 *
 * Recreates the classic 1980 Namco arcade sounds using oscillators.
 * The original hardware used a 3-channel Namco WSG (Waveform Sound Generator)
 * clocked at 96kHz with square waves as the primary waveform.
 *
 * Research sources:
 * - VESWiki frequency data from original arcade ROM
 * - Arduino Pac-Man theme implementation
 * - SoundCy technical analysis
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sirenOsc: OscillatorNode | null = null;
  private sirenGain: GainNode | null = null;
  private sirenPhase = 0;
  private sirenSpeed = 0.08;
  private sirenMinFreq = 220;
  private sirenMaxFreq = 340;
  private sirenAnimationId: number | null = null;
  private muted = false;
  private wakaToggle = false;
  private frightenedOsc: OscillatorNode | null = null;
  private frightenedGain: GainNode | null = null;

  /**
   * Initialize the audio context. Must be called after user interaction.
   */
  init(): void {
    if (this.ctx) return;

    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.25;
    this.masterGain.connect(this.ctx.destination);
  }

  /**
   * Resume audio context if suspended (required by browsers after user gesture)
   */
  async resume(): Promise<void> {
    if (this.ctx?.state === 'suspended') {
      await this.ctx.resume();
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : 0.25;
    }
    return this.muted;
  }

  /**
   * Check if muted
   */
  isMuted(): boolean {
    return this.muted;
  }

  // ============================================
  // WAKA-WAKA (Dot Eating Sound)
  // ============================================

  /**
   * Play the iconic "waka-waka" dot eating sound.
   * Uses two alternating short tones at ~440Hz base frequency.
   * The original rapidly alternates between two square waves.
   */
  playWaka(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.065;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'square';

    // Alternate between two short tones - the original "waka" sound
    // Uses frequencies around 440Hz with slight pitch bend
    if (this.wakaToggle) {
      // "Wa" - slight upward sweep
      osc.frequency.setValueAtTime(393, now); // G4
      osc.frequency.linearRampToValueAtTime(523, now + duration); // C5
    } else {
      // "Ka" - slight downward sweep
      osc.frequency.setValueAtTime(523, now); // C5
      osc.frequency.linearRampToValueAtTime(393, now + duration); // G4
    }
    this.wakaToggle = !this.wakaToggle;

    // Sharp attack, quick decay for staccato effect
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + duration + 0.01);
  }

  // ============================================
  // SIREN (Background Sound)
  // ============================================

  /**
   * Start the background siren sound.
   * Based on original ROM data: frequency rises from ~157 to ~213 register values
   * then descends, creating the characteristic warbling effect.
   * The siren has a smooth, continuous warble like a slow police siren.
   */
  startSiren(): void {
    if (!this.ctx || !this.masterGain || this.sirenOsc) return;

    this.sirenOsc = this.ctx.createOscillator();
    this.sirenGain = this.ctx.createGain();

    // Original uses square wave
    this.sirenOsc.type = 'square';
    this.sirenOsc.frequency.value = this.sirenMinFreq;

    this.sirenGain.gain.value = this.muted ? 0 : 0.07;

    this.sirenOsc.connect(this.sirenGain);
    this.sirenGain.connect(this.masterGain);
    this.sirenOsc.start();

    this.sirenPhase = 0;
    this.animateSiren();
  }

  /**
   * Animate the siren frequency oscillation.
   * Creates a smooth up-down warble pattern.
   */
  private animateSiren(): void {
    if (!this.sirenOsc || !this.ctx) return;

    this.sirenPhase += this.sirenSpeed;
    if (this.sirenPhase > Math.PI * 2) {
      this.sirenPhase -= Math.PI * 2;
    }

    // Triangle wave pattern for smoother warble (up then down)
    // This creates the characteristic Pac-Man siren sound
    const t = this.sirenPhase / (Math.PI * 2);
    const triangle = t < 0.5 ? t * 2 : 2 - t * 2; // 0->1->0
    const freq = this.sirenMinFreq + triangle * (this.sirenMaxFreq - this.sirenMinFreq);
    this.sirenOsc.frequency.setValueAtTime(freq, this.ctx.currentTime);

    this.sirenAnimationId = requestAnimationFrame(() => this.animateSiren());
  }

  /**
   * Update siren parameters based on remaining pellets.
   * As dots are eaten, the siren gets faster and higher pitched.
   */
  updateSirenSpeed(pelletsRemaining: number, totalPellets: number): void {
    const progress = 1 - pelletsRemaining / totalPellets;
    // Speed increases as game progresses (warble gets faster)
    this.sirenSpeed = 0.06 + progress * 0.14;
    // Frequency range shifts upward and widens
    this.sirenMinFreq = 180 + progress * 120;
    this.sirenMaxFreq = 280 + progress * 180;
  }

  /**
   * Stop the background siren
   */
  stopSiren(): void {
    if (this.sirenAnimationId) {
      cancelAnimationFrame(this.sirenAnimationId);
      this.sirenAnimationId = null;
    }
    if (this.sirenOsc) {
      this.sirenOsc.stop();
      this.sirenOsc.disconnect();
      this.sirenOsc = null;
    }
    if (this.sirenGain) {
      this.sirenGain.disconnect();
      this.sirenGain = null;
    }
  }

  // ============================================
  // POWER PELLET & FRIGHTENED MODE
  // ============================================

  private frightenedPhase = 0;
  private frightenedAnimationId: number | null = null;

  /**
   * Play sound when eating a power pellet.
   * The power pellet itself doesn't have a distinct sound - it just
   * triggers the frightened mode background sound.
   */
  playPowerPellet(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    // Switch to frightened mode sound immediately
    this.startFrightenedMode();
  }

  /**
   * Start the frightened mode background sound (replaces normal siren).
   * This is a faster, lower-pitched warble that creates urgency.
   * Similar to the normal siren but more intense.
   */
  startFrightenedMode(): void {
    if (!this.ctx || !this.masterGain) return;

    // Mute regular siren
    if (this.sirenGain) {
      this.sirenGain.gain.value = 0;
    }

    // Stop existing frightened oscillator if any
    if (this.frightenedOsc) {
      this.frightenedOsc.stop();
      this.frightenedOsc.disconnect();
    }
    if (this.frightenedAnimationId) {
      cancelAnimationFrame(this.frightenedAnimationId);
    }

    this.frightenedOsc = this.ctx.createOscillator();
    this.frightenedGain = this.ctx.createGain();

    // Square wave like the normal siren
    this.frightenedOsc.type = 'square';
    this.frightenedOsc.frequency.value = 120;

    this.frightenedGain.gain.value = this.muted ? 0 : 0.08;

    this.frightenedOsc.connect(this.frightenedGain);
    this.frightenedGain.connect(this.masterGain);

    this.frightenedOsc.start();
    this.frightenedPhase = 0;
    this.animateFrightened();
  }

  /**
   * Animate the frightened mode sound - faster warble than normal siren
   */
  private animateFrightened(): void {
    if (!this.frightenedOsc || !this.ctx) return;

    // Faster warble speed than normal siren
    this.frightenedPhase += 0.18;
    if (this.frightenedPhase > Math.PI * 2) {
      this.frightenedPhase -= Math.PI * 2;
    }

    // Triangle wave pattern, lower frequency range
    const t = this.frightenedPhase / (Math.PI * 2);
    const triangle = t < 0.5 ? t * 2 : 2 - t * 2;
    // Lower frequency range (100-180 Hz) for more ominous feel
    const freq = 100 + triangle * 80;
    this.frightenedOsc.frequency.setValueAtTime(freq, this.ctx.currentTime);

    this.frightenedAnimationId = requestAnimationFrame(() => this.animateFrightened());
  }

  /**
   * Stop frightened mode and resume normal siren
   */
  stopFrightenedMode(): void {
    if (this.frightenedAnimationId) {
      cancelAnimationFrame(this.frightenedAnimationId);
      this.frightenedAnimationId = null;
    }
    if (this.frightenedOsc) {
      this.frightenedOsc.stop();
      this.frightenedOsc.disconnect();
      this.frightenedOsc = null;
    }
    if (this.frightenedGain) {
      this.frightenedGain.disconnect();
      this.frightenedGain = null;
    }

    // Resume regular siren
    if (this.sirenGain && !this.muted) {
      this.sirenGain.gain.value = 0.07;
    }
  }

  // ============================================
  // GHOST EATEN
  // ============================================

  /**
   * Play sound when Pac-Man eats a ghost.
   * This is a short warbling sound similar to a quick police siren burst.
   * Based on emulator analysis, the frequency is higher (multiplied by 8).
   */
  playGhostEaten(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.55;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'square';

    // Create a warbling pattern - quick oscillations up and down
    // Higher frequency range (per emulator docs: 3 octaves higher)
    const baseFreq = 600;
    const range = 400;
    const wobbles = 6;

    // Schedule the warble pattern
    for (let i = 0; i <= wobbles * 2; i++) {
      const t = now + (i * duration) / (wobbles * 2);
      // Alternating high-low pattern
      const freq = i % 2 === 0 ? baseFreq + range : baseFreq;
      osc.frequency.setValueAtTime(freq, t);
    }

    gain.gain.setValueAtTime(0.25, now);
    gain.gain.setValueAtTime(0.25, now + duration * 0.8);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + duration + 0.01);
  }

  // ============================================
  // DEATH SOUND
  // ============================================

  /**
   * Play the iconic Pac-Man death sound.
   * Descending frequency sweep from ~1000 Hz to ~200 Hz.
   */
  playDeath(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    // Stop all other sounds
    this.stopSiren();
    this.stopFrightenedMode();

    const now = this.ctx.currentTime;
    const duration = 1.5;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'square';

    // Stepped descent like the original - frequency drops in discrete steps
    // Based on ROM data analysis showing stepped portamento
    const startFreq = 1000;
    const endFreq = 200;
    const steps = 16;

    for (let i = 0; i <= steps; i++) {
      const t = now + (i * duration * 0.85) / steps;
      // Logarithmic descent feels more natural
      const ratio = i / steps;
      const freq = startFreq * Math.pow(endFreq / startFreq, ratio);
      osc.frequency.setValueAtTime(freq, t);
    }

    // Final low note
    osc.frequency.setValueAtTime(endFreq, now + duration * 0.85);
    osc.frequency.linearRampToValueAtTime(100, now + duration);

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.setValueAtTime(0.3, now + duration * 0.8);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + duration + 0.1);
  }

  // ============================================
  // INTRO / READY JINGLE
  // ============================================

  /**
   * Play the game start jingle.
   * Based on the original Pac-Man intro theme in B major at ~105 BPM.
   * Notes: B4-B5-F#5-D#5-B5-F#5-D#5 | C5-C6-G5-E5-C6-G5-E5
   */
  playIntro(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const bpm = 105;
    const sixteenth = 60 / bpm / 4;

    // Pac-Man intro melody (simplified but accurate to original)
    // Based on Arduino implementation and sheet music analysis
    const notes = [
      // First phrase: B4-B5-F#5-D#5-B5-F#5-D#5
      { freq: 494, time: 0, dur: sixteenth },                    // B4
      { freq: 988, time: sixteenth, dur: sixteenth },            // B5
      { freq: 740, time: sixteenth * 2, dur: sixteenth },        // F#5
      { freq: 622, time: sixteenth * 3, dur: sixteenth },        // D#5
      { freq: 988, time: sixteenth * 4, dur: sixteenth * 0.5 },  // B5 (32nd)
      { freq: 740, time: sixteenth * 4.5, dur: sixteenth * 1.5 },// F#5 (dotted 16th)
      { freq: 622, time: sixteenth * 6, dur: sixteenth * 2 },    // D#5 (8th)

      // Second phrase: C5-C6-G5-E5-C6-G5-E5
      { freq: 523, time: sixteenth * 8, dur: sixteenth },        // C5
      { freq: 1047, time: sixteenth * 9, dur: sixteenth },       // C6
      { freq: 784, time: sixteenth * 10, dur: sixteenth },       // G5
      { freq: 659, time: sixteenth * 11, dur: sixteenth },       // E5
      { freq: 1047, time: sixteenth * 12, dur: sixteenth * 0.5 },// C6 (32nd)
      { freq: 784, time: sixteenth * 12.5, dur: sixteenth * 1.5 },// G5 (dotted 16th)
      { freq: 659, time: sixteenth * 14, dur: sixteenth * 2 },   // E5 (8th)
    ];

    notes.forEach(note => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();

      osc.type = 'square';
      osc.frequency.value = note.freq;

      // Sharp attack for chiptune feel
      gain.gain.setValueAtTime(0.25, now + note.time);
      gain.gain.setValueAtTime(0.25, now + note.time + note.dur * 0.8);
      gain.gain.linearRampToValueAtTime(0, now + note.time + note.dur);

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(now + note.time);
      osc.stop(now + note.time + note.dur + 0.01);
    });
  }

  // ============================================
  // LEVEL COMPLETE
  // ============================================

  /**
   * Play level complete sound - uses the intermission melody pattern
   */
  playLevelComplete(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    this.stopSiren();
    this.stopFrightenedMode();

    const now = this.ctx.currentTime;
    const noteDur = 0.12;

    // Quick victory arpeggio based on original intermission
    const notes = [
      { freq: 523, time: 0, dur: noteDur },           // C5
      { freq: 659, time: noteDur, dur: noteDur },     // E5
      { freq: 784, time: noteDur * 2, dur: noteDur }, // G5
      { freq: 1047, time: noteDur * 3, dur: noteDur * 2 }, // C6
      { freq: 784, time: noteDur * 5, dur: noteDur }, // G5
      { freq: 1047, time: noteDur * 6, dur: noteDur * 3 }, // C6 (held)
    ];

    notes.forEach(note => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();

      osc.type = 'square';
      osc.frequency.value = note.freq;

      gain.gain.setValueAtTime(0.25, now + note.time);
      gain.gain.setValueAtTime(0.25, now + note.time + note.dur * 0.7);
      gain.gain.linearRampToValueAtTime(0, now + note.time + note.dur);

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(now + note.time);
      osc.stop(now + note.time + note.dur + 0.01);
    });
  }

  // ============================================
  // EXTRA LIFE
  // ============================================

  /**
   * Play extra life awarded sound (1UP)
   * Based on original ROM data for 10,000 point bonus
   */
  playExtraLife(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const noteDur = 0.08;

    // Quick ascending arpeggio
    const notes = [
      { freq: 523, time: 0, dur: noteDur },           // C5
      { freq: 659, time: noteDur, dur: noteDur },     // E5
      { freq: 784, time: noteDur * 2, dur: noteDur }, // G5
      { freq: 1047, time: noteDur * 3, dur: noteDur },// C6
      { freq: 1319, time: noteDur * 4, dur: noteDur * 2 }, // E6
    ];

    notes.forEach(note => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();

      osc.type = 'square';
      osc.frequency.value = note.freq;

      gain.gain.setValueAtTime(0.2, now + note.time);
      gain.gain.setValueAtTime(0.2, now + note.time + note.dur * 0.7);
      gain.gain.linearRampToValueAtTime(0, now + note.time + note.dur);

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(now + note.time);
      osc.stop(now + note.time + note.dur + 0.01);
    });
  }

  // ============================================
  // FRUIT EATEN
  // ============================================

  /**
   * Play sound when eating fruit bonus
   */
  playFruitEaten(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'square';
    // Quick ascending chirp
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);

    gain.gain.setValueAtTime(0.2, now);
    gain.gain.linearRampToValueAtTime(0, now + 0.15);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.2);
  }

  // ============================================
  // CLEANUP
  // ============================================

  /**
   * Stop all sounds and cleanup
   */
  stopAll(): void {
    this.stopSiren();
    this.stopFrightenedMode();
  }

  /**
   * Destroy audio context
   */
  destroy(): void {
    this.stopAll();
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
    }
  }
}

// Export singleton instance
export const audioManager = new AudioManager();
